import * as React from 'react'
import { Check, PlusCircle, Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * Picks the side a menu opens toward, so it never leaves the viewport.
 *
 * A fixed anchor cannot work for a row of triggers: left-anchored menus run off
 * the right edge for the last filter, and right-anchored ones run off the left
 * edge for the first. Measuring at open time is the only thing that holds for
 * both ends of the row at any window width.
 */
function useMenuAlign(open: boolean, ref: React.RefObject<HTMLElement>, menuWidth: number) {
  const [alignRight, setAlignRight] = React.useState(false)
  React.useLayoutEffect(() => {
    if (!open || !ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const overflowsRight = rect.left + menuWidth > window.innerWidth - 16
    // Only flip when the other side actually has room, or a narrow window would
    // trade one clipped edge for the other.
    setAlignRight(overflowsRight && rect.right - menuWidth > 16)
  }, [open, ref, menuWidth])
  return alignRight ? 'right-0' : 'left-0'
}

/** One selected value, shown inside the trigger. */
function FilterValue({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-secondary px-1.5 py-0.5 text-[0.7rem] font-normal text-secondary-foreground">
      {children}
    </span>
  )
}

/**
 * The button every filter opens from.
 *
 * Sized to its own content, and dashed while empty. Equal-width boxes made the
 * row read as a form to fill in rather than a set of refinements, and gave an
 * unset filter the same visual weight as one doing work — so nothing about the
 * row told you what was actually applied. Dashed-when-empty is the opposite:
 * the eye lands only on filters that are on.
 */
function FilterTrigger({
  label, active, open, onToggle, onClear, children,
}: {
  label: string
  active: boolean
  open: boolean
  onToggle: () => void
  onClear?: () => void
  children?: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-label={label}
      className={cn(
        'inline-flex h-8 max-w-full items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-border bg-background font-medium hover:bg-accent/50'
          : 'border-dashed border-input text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {!active && <PlusCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      <span className="shrink-0">{label}</span>
      {active && (
        <>
          <span className="h-3.5 w-px shrink-0 bg-border" aria-hidden />
          <span className="flex min-w-0 items-center gap-1 truncate">{children}</span>
          {onClear && (
            <span
              role="button"
              tabIndex={0}
              aria-label={`清除${label}筛选`}
              onClick={(e) => { e.stopPropagation(); onClear() }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClear() }
              }}
              className="shrink-0 rounded text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" aria-hidden />
            </span>
          )}
        </>
      )}
    </button>
  )
}

export interface FilterOption {
  value: string
  label: string
  /** Secondary line, shown under the label. */
  description?: string
  /** Right-aligned annotation, typically a row count. */
  badge?: string
}

export interface FilterSelectProps {
  label: string
  options: FilterOption[]
  selected: string[]
  onToggle: (value: string) => void
  onClear?: () => void
  /** Shows the search box; defaults to on once the list is long enough to need it. */
  searchable?: boolean
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  /** Fixed menu width; defaults to a little wider than the trigger. */
  menuClassName?: string
}

/**
 * Multi-select filter dropdown sharing the rule builder's field-picker shape:
 * a plain trigger, a floating panel with its own search box, and rows carrying
 * a label, a description and a badge.
 *
 * Filters used to be chips laid flat inside an expandable panel, which meant
 * the page had two unrelated vocabularies for "choose from a list" depending on
 * where you were standing. One component keeps them the same thing.
 */
export function FilterSelect({
  label,
  options,
  selected,
  onToggle,
  onClear,
  searchable,
  searchPlaceholder = '搜索…',
  emptyText = '没有匹配项',
  className,
  menuClassName,
}: FilterSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const rootRef = React.useRef<HTMLDivElement>(null)

  // Close on outside click or Escape. Without this the panel survives a click
  // on the next filter along, and two menus overlap.
  React.useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  React.useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const showSearch = searchable ?? options.length > 8
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => `${o.label}${o.description ?? ''}`.toLowerCase().includes(q))
  }, [options, query])

  const chosen = options.filter((o) => selected.includes(o.value))
  const align = useMenuAlign(open, rootRef, 320)

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <FilterTrigger
        label={label}
        active={selected.length > 0}
        open={open}
        onToggle={() => setOpen((c) => !c)}
        onClear={onClear}
      >
        {/* Up to two values inline; beyond that a count, because the row must
            stay readable when several filters are set at once. */}
        {chosen.length > 2 ? (
          <FilterValue>{chosen.length} 项</FilterValue>
        ) : (
          chosen.map((o) => <FilterValue key={o.value}>{o.label}</FilterValue>)
        )}
      </FilterTrigger>

      {open && (
        <div
          className={cn(
            'absolute top-10 z-30 w-[min(320px,calc(100vw-3rem))] rounded-xl border bg-popover p-2 shadow-xl',
            align,
            menuClassName,
          )}
        >
          {showSearch && (
            <div className="mb-2 flex items-center gap-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9"
                />
              </div>
              <Button variant="ghost" size="icon-sm" onClick={() => setOpen(false)} aria-label={`关闭${label}选择`}>
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          )}
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {filtered.map((option) => {
              const active = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onToggle(option.value)}
                  className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-accent"
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                      active ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}
                    aria-hidden
                  >
                    {active && <Check className="h-3 w-3" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{option.label}</span>
                    {option.description && (
                      <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                    )}
                  </span>
                  {option.badge && (
                    <Badge variant="muted" className="shrink-0 text-[10px] tabular-nums">{option.badge}</Badge>
                  )}
                </button>
              )
            })}
            {!filtered.length && <p className="p-4 text-center text-xs text-muted-foreground">{emptyText}</p>}
          </div>
        </div>
      )}
    </div>
  )
}

export interface FilterPopoverProps {
  label: string
  /** Whether the trigger should read as carrying an active filter. */
  active?: boolean
  /** Trigger text when a filter is set. */
  summary?: string
  onClear?: () => void
  className?: string
  menuClassName?: string
  children: React.ReactNode
}

/**
 * The same trigger and panel as FilterSelect, but with arbitrary contents —
 * for filters that are ranges rather than choices.
 */
export function FilterPopover({
  label, active, summary, onClear, className, menuClassName, children,
}: FilterPopoverProps) {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!open) return
    function onPointerDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const align = useMenuAlign(open, rootRef, 340)

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <FilterTrigger
        label={label}
        active={Boolean(active)}
        open={open}
        onToggle={() => setOpen((c) => !c)}
        onClear={onClear}
      >
        {summary && <FilterValue>{summary}</FilterValue>}
      </FilterTrigger>
      {open && (
        <div
          className={cn(
            'absolute top-10 z-30 w-[min(340px,calc(100vw-3rem))] rounded-xl border bg-popover p-3 shadow-xl',
            align,
            menuClassName,
          )}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export interface FilterToggleProps {
  label: string
  active: boolean
  onToggle: () => void
  className?: string
}

/**
 * An on/off filter wearing the same trigger as the dropdown ones.
 *
 * Booleans have no menu to open and no value to display, but they belong in the
 * filter row rather than somewhere else in the page: they compose with the
 * others, and a filter kept apart from its neighbours is one you have to
 * remember is set.
 */
export function FilterToggle({ label, active, onToggle, className }: FilterToggleProps) {
  return (
    <div className={cn('relative', className)}>
      <FilterTrigger label={label} active={active} open={false} onToggle={onToggle} />
    </div>
  )
}
